import { Check, ChevronRight } from 'lucide-react';
import { ContextMenu as RCM, DropdownMenu as RDM, type Menubar as RMB } from 'radix-ui';
import type { ReactElement, ReactNode } from 'react';
import { Kbd } from './Kbd.js';
import './overlay.css';

/** Declarative menu model shared by Menu and ContextMenu. */
export type MenuEntry =
  | {
      type?: 'item';
      id: string;
      label: string;
      icon?: ReactNode;
      shortcut?: readonly string[];
      /** Count or short status after the label (e.g. overdue items). */
      badge?: ReactNode;
      /** Destructive items render in the danger colour. */
      tone?: 'danger';
      disabled?: boolean;
      /** Greyed but still selectable (a locked add-on that opens its upsell). */
      muted?: boolean;
      /** Tooltip; on a disabled item, why it is unavailable. */
      title?: string;
      onSelect: () => void;
    }
  | {
      type: 'checkbox';
      id: string;
      label: string;
      checked: boolean;
      disabled?: boolean;
      onCheckedChange: (checked: boolean) => void;
    }
  | { type: 'separator'; id: string }
  | { type: 'label'; id: string; label: string }
  | { type: 'submenu'; id: string; label: string; icon?: ReactNode; items: readonly MenuEntry[] };

type Parts = typeof RDM | typeof RCM | typeof RMB;

/** Renders menu entries with one Radix menu family (dropdown, context menu or menubar). */
export function renderEntries(P: Parts, entries: readonly MenuEntry[]): ReactNode {
  return entries.map((e) => {
    switch (e.type) {
      case 'separator':
        return <P.Separator key={e.id} className="vel-option-sep" />;
      case 'label':
        return (
          <P.Label key={e.id} className="vel-option-label">
            {e.label}
          </P.Label>
        );
      case 'checkbox':
        return (
          <P.CheckboxItem
            key={e.id}
            className="vel-option"
            checked={e.checked}
            disabled={e.disabled ?? false}
            onCheckedChange={(c: boolean | 'indeterminate') => e.onCheckedChange(c === true)}
            onSelect={(ev: Event) => ev.preventDefault()}
          >
            <span className="vel-option__icon" style={{ width: 14 }}>
              <P.ItemIndicator>
                <Check size={14} aria-hidden />
              </P.ItemIndicator>
            </span>
            {e.label}
          </P.CheckboxItem>
        );
      case 'submenu':
        return (
          <P.Sub key={e.id}>
            <P.SubTrigger className="vel-option">
              {e.icon ? <span className="vel-option__icon">{e.icon}</span> : null}
              {e.label}
              <span className="vel-option__hint">
                <ChevronRight size={14} aria-hidden />
              </span>
            </P.SubTrigger>
            <P.Portal>
              <P.SubContent className="vel-float" sideOffset={4} alignOffset={-4}>
                {renderEntries(P, e.items)}
              </P.SubContent>
            </P.Portal>
          </P.Sub>
        );
      default:
        return (
          <P.Item
            key={e.id}
            className="vel-option"
            data-tone={e.tone}
            data-muted={e.muted ? '' : undefined}
            disabled={e.disabled ?? false}
            {...(e.title ? { title: e.title } : {})}
            onSelect={e.onSelect}
          >
            {e.icon ? <span className="vel-option__icon">{e.icon}</span> : null}
            {e.label}
            {e.badge !== undefined && e.badge !== null ? (
              <span className="vel-option__badge">{e.badge}</span>
            ) : null}
            {e.shortcut ? (
              <span className="vel-option__hint">
                <Kbd keys={e.shortcut} />
              </span>
            ) : null}
          </P.Item>
        );
    }
  });
}

export interface MenuProps {
  trigger: ReactElement;
  items: readonly MenuEntry[];
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Controlled open state (a keyboard shortcut opens it); uncontrolled when omitted. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** A heading inside the menu, above the items (screen readers name the menu by it). */
  'aria-label'?: string;
}

/** Dropdown menu opened from a button. */
export function Menu({
  trigger,
  items,
  align = 'start',
  side = 'bottom',
  open,
  onOpenChange,
  'aria-label': ariaLabel,
}: MenuProps) {
  return (
    <RDM.Root {...(open !== undefined ? { open } : {})} {...(onOpenChange ? { onOpenChange } : {})}>
      <RDM.Trigger asChild>{trigger}</RDM.Trigger>
      <RDM.Portal>
        <RDM.Content
          className="vel-float"
          align={align}
          side={side}
          sideOffset={6}
          collisionPadding={8}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        >
          {renderEntries(RDM, items)}
        </RDM.Content>
      </RDM.Portal>
    </RDM.Root>
  );
}

export interface ContextMenuProps {
  items: readonly MenuEntry[];
  /** The region that opens the menu on right-click, Menu key or Shift+F10. */
  children: ReactElement;
}

export function ContextMenu({ items, children }: ContextMenuProps) {
  return (
    <RCM.Root>
      <RCM.Trigger asChild>{children}</RCM.Trigger>
      <RCM.Portal>
        <RCM.Content className="vel-float" collisionPadding={8}>
          {renderEntries(RCM, items)}
        </RCM.Content>
      </RCM.Portal>
    </RCM.Root>
  );
}
